import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import useSetupChecks from 'components/CompanySetupChecklist';
import { getActiveCompanyId } from 'utils/companyContext';
import GeneralInformationSection from './GeneralInformationSection';
import FederalStateTaxesSection from './FederalStateTaxesSection';
import ManagePaySchedules from './ManagePaySchedules';
import ManageWorkplaces from './ManageWorkplaces';
import ManageDepartments from './ManageDepartments';
import WithdrawalBankAccountSection from './WithdrawalBankAccountSection';
import EmployeeSetupSection from './EmployeeSetupSection';
import WorkersCompensationSection from './WorkersCompensationSection';
import PriorPayrollSection from './PriorPayrollSection';
import FilingAuthorizationSection from './FilingAuthorizationSection';

const stepStorageKey = (companyId) => `getReadyWizardStep:${companyId || 'none'}`;

const WIZARD_STEPS = [
    {
        id: 'company-info',
        label: 'Company Information',
        description: 'Legal name, EIN, and address',
        required: true,
        Component: GeneralInformationSection,
        editProps: { startInEditMode: true },
        tip: 'Click Save Changes before Next so progress updates.',
        incompleteHint: 'Click Save Changes on this form, then click Next — or skip for now.',
    },
    {
        id: 'federal-taxes',
        label: 'Federal & State Taxes',
        description: 'Federal deposit schedule and SUTA rate',
        required: true,
        Component: FederalStateTaxesSection,
        editProps: { startInEditMode: true },
        tip: 'Click Save Changes before Next so progress updates.',
        incompleteHint: 'Click Save Changes on this form, then click Next — or skip for now.',
    },
    {
        id: 'pay-schedules',
        label: 'Pay Schedules',
        description: 'Add at least one pay schedule',
        required: true,
        Component: ManagePaySchedules,
        // No Save on this step — add via UI, or skip and continue
        allowNextWhenIncomplete: true,
        tip: 'Add a schedule with the + button. Employer self-onboarding creates one when a pay schedule was selected.',
        incompleteHint: 'Add at least one pay schedule, then click Next — or skip for now.',
    },
    {
        id: 'workplaces',
        label: 'Workplaces',
        description: 'Add at least one workplace location',
        required: true,
        Component: ManageWorkplaces,
        allowNextWhenIncomplete: true,
        tip: 'Employer self-onboarding creates an HQ workplace from the company address when available.',
        incompleteHint: 'Add at least one workplace, then click Next — or skip for now.',
    },
    {
        id: 'employees',
        label: 'Employees',
        description: 'Add at least one active employee',
        required: true,
        Component: EmployeeSetupSection,
        allowNextWhenIncomplete: true,
        tip: 'Click Manage Employees to add someone, then return here — your step is saved. Employees are not created by company self-onboarding.',
        incompleteHint: 'Add at least one active employee (Manage Employees / Add Employee), then click Next — or skip for now.',
    },
    {
        id: 'bank-account',
        label: 'Withdrawal Bank Account',
        description: 'Company bank for payroll ACH',
        required: true,
        Component: WithdrawalBankAccountSection,
        allowNextWhenIncomplete: true,
        tip: 'A bank on file completes this checklist step. Verified Dwolla funding is still required before ACH approve.',
        incompleteHint: 'Add a company withdrawal bank account, then click Next — or skip for now.',
    },
    {
        id: 'departments',
        label: 'Departments',
        description: 'Optional employee grouping',
        required: false,
        Component: ManageDepartments,
        allowNextWhenIncomplete: true,
    },
    {
        id: 'employee-setup',
        label: 'Employee Direct Deposit',
        description: 'Optional — employee bank accounts for ACH',
        required: false,
        Component: EmployeeSetupSection,
        allowNextWhenIncomplete: true,
    },
    {
        id: 'workers-comp',
        label: 'Workers Compensation',
        description: 'Optional carrier and policy',
        required: false,
        Component: WorkersCompensationSection,
        editProps: { startInEditMode: true },
        tip: 'Click Save Changes before Next if you edit this section.',
        incompleteHint: 'Click Save Changes if you edited this section, then Next — or skip for now.',
    },
    {
        id: 'prior-payroll',
        label: 'Prior Payroll (YTD)',
        description: 'Optional — only if switching mid-year',
        required: false,
        Component: PriorPayrollSection,
        editProps: { startInEditMode: true },
        tip: 'Click Save Changes before Next if you edit this section.',
        incompleteHint: 'Click Save Changes if you edited this section, then Next — or skip for now.',
    },
    {
        id: 'filing-auth',
        label: 'Filing Authorization',
        description: 'Optional authorized signatory',
        required: false,
        Component: FilingAuthorizationSection,
        allowNextWhenIncomplete: true,
    },
];

/**
 * Guided company setup wizard — embeds Manage Company sections with Back / Next / Skip / Finish.
 */
function GetReadyForPayroll() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const companyId = getActiveCompanyId();
    const { checks, allRequiredDone, requiredDone, requiredTotal } = useSetupChecks();

    const queryKeys = useMemo(() => ([
        ['company', companyId],
        ['company-bank-accounts', companyId],
        ['company-ach-settings', companyId],
        ['pay-schedules', companyId],
        ['workplaces', companyId],
        ['employees', companyId],
        ['departments', companyId],
        ['signatories', companyId],
        ['ytd-entries', companyId],
    ]), [companyId]);

    const doneById = useMemo(() => {
        const map = {};
        checks.forEach(c => { map[c.id] = c.done; });
        return map;
    }, [checks]);

    const firstIncomplete = useMemo(() => {
        const idx = WIZARD_STEPS.findIndex(s => s.required && !doneById[s.id]);
        return idx >= 0 ? idx : 0;
    }, [doneById]);

    const [stepIndex, setStepIndex] = useState(-1);
    const [blockMsg, setBlockMsg] = useState('');
    const [finished, setFinished] = useState(false);
    const [advancing, setAdvancing] = useState(false);
    const [pendingAdvance, setPendingAdvance] = useState(false);

    // Restore wizard step per company (avoid cross-company bleed)
    useEffect(() => {
        setFinished(false);
        setBlockMsg('');
        setPendingAdvance(false);
        setAdvancing(false);
        try {
            const saved = sessionStorage.getItem(stepStorageKey(companyId));
            if (saved != null) {
                const n = parseInt(saved, 10);
                if (!Number.isNaN(n) && n >= 0 && n < WIZARD_STEPS.length) {
                    setStepIndex(n);
                    return;
                }
            }
        } catch { /* ignore */ }
        setStepIndex(-1);
    }, [companyId]);

    // Jump straight to done screen when required setup already complete (and no mid-wizard resume)
    useEffect(() => {
        if (checks.length > 0 && allRequiredDone && stepIndex >= 0) {
            try {
                const saved = sessionStorage.getItem(stepStorageKey(companyId));
                if (saved == null) setFinished(true);
            } catch { /* ignore */ }
        }
    }, [checks.length, allRequiredDone, stepIndex, companyId]);

    useEffect(() => {
        if (stepIndex < 0 && checks.length > 0) setStepIndex(firstIncomplete);
    }, [stepIndex, checks.length, firstIncomplete]);

    useEffect(() => {
        if (stepIndex < 0 || !companyId) return;
        try { sessionStorage.setItem(stepStorageKey(companyId), String(stepIndex)); } catch { /* ignore */ }
    }, [stepIndex, companyId]);

    const safeIndex = stepIndex < 0 ? 0 : stepIndex;
    const step = WIZARD_STEPS[safeIndex];
    const isLast = safeIndex === WIZARD_STEPS.length - 1;
    const stepDone = !!doneById[step.id];
    const StepBody = step.Component;
    const pct = requiredTotal > 0
        ? Math.round((requiredDone / requiredTotal) * 100)
        : Math.round(((safeIndex + 1) / WIZARD_STEPS.length) * 100);

    const clearStepStorage = () => {
        try { sessionStorage.removeItem(stepStorageKey(companyId)); } catch { /* ignore */ }
    };

    const finishWizard = () => {
        clearStepStorage();
        setFinished(true);
        setPendingAdvance(false);
        setAdvancing(false);
    };

    const moveForward = (from) => {
        if (from >= WIZARD_STEPS.length - 1) finishWizard();
        else setStepIndex(from + 1);
    };

    // After Next + refetch, wait for doneById to update then advance (or show block)
    useEffect(() => {
        if (!pendingAdvance) return;
        const current = WIZARD_STEPS[safeIndex];
        const incomplete = current.required && !doneById[current.id];
        // List/link steps have no Save Changes — allow Next to continue (same as Skip)
        if (incomplete && !current.allowNextWhenIncomplete) {
            setPendingAdvance(false);
            setAdvancing(false);
            setBlockMsg(
                current.incompleteHint
                || 'Complete this step, then click Next — or skip for now.'
            );
            return;
        }
        setPendingAdvance(false);
        setAdvancing(false);
        setBlockMsg('');
        moveForward(safeIndex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingAdvance, doneById, safeIndex]);

    const refetchSetup = async () => {
        await Promise.all(queryKeys.map(key => queryClient.invalidateQueries({ queryKey: key })));
        // Also invalidate legacy unscoped keys used by embedded sections
        await Promise.all([
            ['company'], ['company-bank-accounts'], ['company-ach-settings'],
            ['pay-schedules'], ['workplaces'], ['employees'], ['departments'],
            ['signatories'], ['ytd-entries'],
        ].map(key => queryClient.invalidateQueries({ queryKey: key })));
        await Promise.all(queryKeys.map(key => queryClient.refetchQueries({ queryKey: key })));
    };

    const handleNext = async () => {
        setBlockMsg('');
        setAdvancing(true);
        try {
            await refetchSetup();
        } catch { /* ignore */ }
        setPendingAdvance(true);
    };

    const goBack = () => {
        setBlockMsg('');
        setPendingAdvance(false);
        if (safeIndex > 0) setStepIndex(safeIndex - 1);
    };

    const skipStep = () => {
        setBlockMsg('');
        setPendingAdvance(false);
        setAdvancing(false);
        moveForward(safeIndex);
    };

    const skipRemainingOptional = () => {
        setBlockMsg('');
        setPendingAdvance(false);
        if (step.required) skipStep();
        else finishWizard();
    };

    if (finished) {
        return (
            <div className="content" style={{ padding: '24px 28px', maxWidth: 720 }}>
                <div style={{
                    background: '#fff', border: '1px solid #bbf7d0', borderRadius: 12,
                    padding: 28, textAlign: 'center',
                }}>
                    <h2 style={{ margin: '0 0 8px', fontWeight: 800, color: '#15803d', fontSize: 22 }}>
                        {allRequiredDone ? 'You are ready for payroll' : 'Setup saved'}
                    </h2>
                    <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: 14 }}>
                        {allRequiredDone
                            ? 'Required company setup is complete. You can start a draft payroll now.'
                            : `Required progress: ${requiredDone} of ${requiredTotal}. You can finish later or run check-only payroll.`}
                    </p>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/dashboard')}>
                            Dashboard
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => { setFinished(false); setStepIndex(firstIncomplete); }}
                        >
                            Continue setup
                        </button>
                        <button type="button" className="btn btn-primary" onClick={() => navigate('/dashboard/start-new-payroll')}>
                            Start payroll
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (stepIndex < 0) {
        return (
            <div className="content" style={{ padding: '24px 28px' }}>
                <p style={{ color: '#6b7280' }}>Loading setup…</p>
            </div>
        );
    }

    return (
        <div className="content" style={{ padding: '24px 28px', maxWidth: 960 }}>
            <div style={{ marginBottom: 16 }}>
                <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                    <button
                        type="button"
                        onClick={() => navigate('/dashboard')}
                        style={{ background: 'none', border: 'none', color: '#1B75F0', cursor: 'pointer', padding: 0, fontSize: 13 }}
                    >
                        Dashboard
                    </button>
                    {' / Get ready for payroll'}
                </p>
                <h2 style={{ margin: '8px 0 4px', fontWeight: 800, color: '#1a3a5c', fontSize: 22 }}>
                    Get ready for payroll
                </h2>
                <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
                    Step {safeIndex + 1} of {WIZARD_STEPS.length}
                    {step.required ? '' : ' · optional'}
                    {' · '}{requiredDone} of {requiredTotal} required complete
                </p>
            </div>

            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 12, marginBottom: 8 }}>
                {WIZARD_STEPS.map((s, i) => {
                    const done = !!doneById[s.id];
                    const active = i === safeIndex;
                    return (
                        <button
                            key={s.id}
                            type="button"
                            title={s.label}
                            onClick={() => { setBlockMsg(''); setPendingAdvance(false); setStepIndex(i); }}
                            style={{
                                flex: '0 0 auto', minWidth: 28, height: 28, borderRadius: 99,
                                border: active ? '2px solid #1B75F0' : '1px solid #e5e7eb',
                                background: done ? '#16a34a' : (active ? '#EFF6FF' : '#f9fafb'),
                                color: done ? '#fff' : (active ? '#1B75F0' : '#9ca3af'),
                                fontSize: 11, fontWeight: 700, cursor: 'pointer',
                            }}
                        >
                            {done ? '✓' : i + 1}
                        </button>
                    );
                })}
            </div>

            <div style={{ background: '#f3f4f6', borderRadius: 99, height: 8, marginBottom: 20, overflow: 'hidden' }}>
                <div style={{
                    height: '100%', width: `${pct}%`, borderRadius: 99,
                    background: '#1B75F0', transition: 'width 0.3s ease',
                }} />
            </div>

            <div style={{
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
                padding: '20px 22px', marginBottom: 16,
            }}>
                <div style={{ marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a3a5c' }}>
                        {step.label}
                        {!step.required && (
                            <span style={{ fontSize: 13, fontWeight: 500, color: '#9ca3af' }}> (optional)</span>
                        )}
                    </h3>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>{step.description}</p>
                    {step.tip && (
                        <p style={{
                            margin: '8px 0 0', fontSize: 12, color: '#1B75F0',
                            background: '#EFF6FF', padding: '8px 10px', borderRadius: 8,
                        }}>
                            {step.tip}
                        </p>
                    )}
                    {stepDone && (
                        <p style={{ margin: '8px 0 0', fontSize: 12, color: '#15803d', fontWeight: 600 }}>
                            This step looks complete — click Next to continue.
                        </p>
                    )}
                </div>

                <div key={`${companyId}-${step.id}`} style={{ minHeight: 200 }}>
                    <StepBody {...(step.editProps || {})} />
                </div>
            </div>

            {blockMsg && (
                <div style={{
                    background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8,
                    padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#1e3a8a',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                }}>
                    <span>{blockMsg}</span>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={skipStep}>
                        Skip for now
                    </button>
                </div>
            )}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" className="btn btn-outline-secondary" onClick={goBack} disabled={safeIndex === 0}>
                    Back
                </button>
                <div style={{ flex: 1 }} />
                <button type="button" className="btn btn-outline-secondary" onClick={skipStep}>
                    {step.required ? 'Skip for now' : 'Skip'}
                </button>
                {!step.required && (
                    <button type="button" className="btn btn-light" onClick={skipRemainingOptional}>
                        Skip remaining optional
                    </button>
                )}
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={advancing}
                    onClick={handleNext}
                >
                    {advancing ? 'Checking…' : (isLast ? 'Finish' : 'Next')}
                </button>
            </div>
        </div>
    );
}

export default GetReadyForPayroll;
